import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Toggle } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { STATUS_FILTER_OPTIONS, type StatusFilter } from './projects-filters'

/**
 * Filter rail for /projects.
 *
 * Module-scoped, mirrors the MarginsFilterRail (Sprint 09) +
 * InventoryFilterRail (Sprint 09) + CofaFilterRail (Sprint 04)
 * collapse-toggle pattern. Two filter sections:
 *   - **Status** chip group (All / Draft / Testing / Approved / Rejected)
 *   - **Include archived** toggle
 *
 * Constants + filter types are in `./projects-filters.ts` (Fast
 * Refresh contract).
 */

export interface ProjectsFilterRailProps {
  status: StatusFilter
  onStatusChange: (next: StatusFilter) => void
  includeArchived: boolean
  onIncludeArchivedChange: (next: boolean) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

function activeCount(props: ProjectsFilterRailProps): number {
  let n = 0
  if (props.status !== 'all') n += 1
  if (props.includeArchived) n += 1
  return n
}

export function ProjectsFilterRail(props: ProjectsFilterRailProps) {
  const {
    status,
    onStatusChange,
    includeArchived,
    onIncludeArchivedChange,
    collapsed,
    onToggleCollapsed,
  } = props

  if (collapsed) {
    const n = activeCount(props)
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label="Filters (collapsed)"
      >
        <button
          type="button"
          aria-label="Expand filters"
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {n > 0 ? (
          <span
            className="text-role-badge-count mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-accent-text"
            aria-label={`${n} active filter${n === 1 ? '' : 's'}`}
          >
            {n}
          </span>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      className="flex w-[220px] flex-shrink-0 flex-col border-r border-border-subtle bg-surface-sunk"
      aria-label="Filters"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
        <span className="text-role-section-label">Filters</span>
        <button
          type="button"
          aria-label="Collapse filters"
          onClick={onToggleCollapsed}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronLeft size={14} />
        </button>
      </header>

      <div className="flex flex-col gap-3 p-3">
        <section className="flex flex-col gap-1.5">
          <span className="text-role-section-label">Status</span>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTER_OPTIONS.map((opt) => {
              const active = status === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onStatusChange(opt.value)}
                  className={cn(
                    'inline-flex items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-xs text-primary hover:bg-surface-hover',
                    'aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text',
                  )}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </section>

        <section className="flex flex-col gap-1.5">
          <span className="text-role-section-label">Options</span>
          <Toggle
            checked={includeArchived}
            onChange={(e) => onIncludeArchivedChange(e.target.checked)}
            label="Show archived"
          />
        </section>
      </div>
    </aside>
  )
}
